import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined'
import React, { useEffect, useRef, useState } from 'react'
import VolumeOffOutlinedIcon from '@mui/icons-material/VolumeOffOutlined'
import BaseButton from '../buttons/BaseButton'
import SkipButton from '../buttons/SkipButton'
import TogglePlay from '../buttons/TogglePlay'

interface AudioProps {
  src: string
  onNextCalled: () => void
}

const AudioPlayer: React.FC<AudioProps> = ({ src, onNextCalled }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const progressBarRef = useRef<HTMLDivElement | null>(null)
  const animationFrameRef = useRef<number | undefined>(undefined)

  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  const lastVolumeRef = useRef(volume)

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const updateProgress = (): void => {
      if (!audio.paused) {
        setProgress((audio.currentTime / audio.duration) * 100)
        animationFrameRef.current = requestAnimationFrame(updateProgress)
      }
    }

    if (isPlaying) {
      animationFrameRef.current = requestAnimationFrame(updateProgress)
    } else {
      if (animationFrameRef.current !== undefined) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }

    return (): void => {
      if (animationFrameRef.current !== undefined) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [isPlaying])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    // if (audioRef.current) {
    audio
      .play()
      .then(() => setIsPlaying(true))
      .catch(err => console.error('Autoplay failed:', err))
    // }
  }, [src])

  const togglePlay = (): void => {
    if (!audioRef.current) return
    if (isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play()
    }
    setIsPlaying(!isPlaying)
  }

  const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (!audioRef.current) return
    const newVolume = parseFloat(event.target.value)
    audioRef.current.volume = newVolume
    setVolume(newVolume)
    setIsMuted(newVolume === 0)
  }

  const toggleMute = (): void => {
    if (!audioRef.current) return
    if (isMuted) {
      audioRef.current.volume = lastVolumeRef.current
      setVolume(lastVolumeRef.current)
    } else {
      lastVolumeRef.current = volume // Stocker l'ancien volume
      audioRef.current.volume = 0
      setVolume(0)
    }
    setIsMuted(!isMuted)
  }

  const handleProgressClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!audioRef.current || !progressBarRef.current) return

    const rect = progressBarRef.current.getBoundingClientRect()
    const clickX = event.clientX - rect.left
    const width = rect.width
    const newProgress = (clickX / width) * 100
    audioRef.current.currentTime = (newProgress / 100) * audioRef.current.duration
    setProgress(newProgress)
  }

  return (
    <div className="audio-player-container">
      <div className="audio-player">
        <TogglePlay isPlaying={isPlaying} togglePlay={togglePlay} />

        {/* Progress Bar */}
        <div className="progress-bar" ref={progressBarRef} onClick={handleProgressClick}>
          <div className="progress" style={{ width: `${progress}%` }}></div>
        </div>

        <div className="volume-wrapper">
          <BaseButton className="control-button forward-btn" onClick={toggleMute}>
            {isMuted ? <VolumeOffOutlinedIcon /> : <VolumeUpOutlinedIcon />}
          </BaseButton>
          <div className="volume-slider-container">
            <input
              type="range"
              className="volume-slider"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={handleVolumeChange}
            />
          </div>
        </div>

        <audio ref={audioRef} src={src} onEnded={onNextCalled}></audio>
      </div>
      <div className="skip-button-layout">
        <SkipButton onClick={onNextCalled} />
      </div>
    </div>
  )
}

export default AudioPlayer
